import { useMemo, useState, type ReactNode } from 'react'
import { Button, Empty, Field, Icon, Input, Modal, Notice, Select, Table, type ColumnDef } from '../../design/index.js'
import type { FilterDef } from '@project/contracts'
import { useApi } from '../../app/api.js'
import { adminApi } from '../../app/admin-api.js'
import { ColumnsButton, useChosenColumns } from '../../framework/columns.js'
import { SavedViews } from '../../framework/SavedViews.js'
import './admin.css'

/**
 * The reference's list chrome for the administration lists: a Filter panel
 * applied with Apply, Export (with its "This will export all N entries"
 * confirmation), and a pager reading "1-50 of 42".
 *
 * The lists here are small and already in the browser, so filtering and
 * paging happen on screen; Export sends the filtered rows to the server, which
 * writes the same workbook every other export does.
 */

export interface AdminFilter<T> {
  readonly key: string
  readonly label: string
  /** A select offers these; a text box matches as you type it. */
  readonly options?: ReadonlyArray<{ value: string; label: string }>
  readonly matches: (row: T, value: string) => boolean
}

/** A column the chooser can show, hide and order; `optional` starts hidden. */
export interface AdminColumn<T> {
  readonly key: string
  readonly label: string
  readonly width?: number
  readonly optional?: boolean
  readonly locked?: boolean
  readonly render?: (row: T) => ReactNode
}

export interface AdminExportColumn<T> {
  readonly header: string
  readonly value: (row: T) => string | number | null
}

export const textMatch = (haystack: string | null | undefined, needle: string): boolean =>
  (haystack ?? '').toLowerCase().includes(needle.trim().toLowerCase())

export const AdminList = <T,>({
  title, scope, rows, columns, getKey, filters, exportColumns, actions, onRowClick, pageSize = 50, empty,
}: {
  title: string
  /** `admin:<list>` — where its saved views and column choice are kept. */
  scope: string
  rows: readonly T[]
  columns: ReadonlyArray<AdminColumn<T>>
  getKey: (row: T) => string
  filters: ReadonlyArray<AdminFilter<T>>
  exportColumns: ReadonlyArray<AdminExportColumn<T>>
  actions?: ReactNode
  onRowClick?: (row: T) => void
  pageSize?: number
  empty?: ReactNode
}) => {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [applied, setApplied] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [confirming, setConfirming] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chooser = useChosenColumns(scope, columns)
  // The same filters, in the shape the Create View dialog sets them.
  const filterDefs: FilterDef[] = useMemo(() => filters.map((f) => (f.options
    ? { key: f.key, label: f.label, control: 'select' as const, options: f.options }
    : { key: f.key, label: f.label, control: 'text' as const })), [filters])
  const tableColumns: ReadonlyArray<ColumnDef<T>> = chooser.visible.map((c) => ({
    key: c.key, header: c.label, ...(c.width ? { width: c.width } : {}), ...(c.render ? { render: c.render } : {}),
  }))
  const applyView = (view: { filters: Readonly<Record<string, string>>; columns: readonly string[] }): void => {
    setDraft({ ...view.filters })
    setApplied({ ...view.filters })
    setPage(1)
    if (view.columns.length > 0) chooser.choose(view.columns)
  }

  const shown = useMemo(() => rows.filter((row) => filters.every((f) => {
    const v = applied[f.key]
    return v === undefined || v === '' || f.matches(row, v)
  })), [rows, filters, applied])

  const pages = Math.max(1, Math.ceil(shown.length / pageSize))
  const current = Math.min(page, pages)
  const slice = shown.slice((current - 1) * pageSize, current * pageSize)
  const from = shown.length === 0 ? 0 : (current - 1) * pageSize + 1
  const to = Math.min(shown.length, current * pageSize)
  const active = Object.values(applied).filter((v) => v !== '').length

  const runExport = async (): Promise<void> => {
    setExporting(true)
    try {
      await adminApi(api).exportRows(title, exportColumns.map((c) => c.header), shown.map((r) => exportColumns.map((c) => c.value(r))))
      setConfirming(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The export failed.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <div className="row row--wrap" style={{ margin: '20px 0 12px', justifyContent: 'space-between' }}>
        <div className="row row--wrap">
          {/*
            SET1-044 — the full Create View dialog, as the master lists have it:
            name, who sees it, default, the columns to add and the filters.
          */}
          <SavedViews scope={scope} filters={applied} columns={chooser.chosen} onApply={applyView} onDefault={applyView}
            editor={{
              columns,
              currentColumns: chooser.visible.map((c) => c.key),
              filters: filterDefs,
              currentFilters: applied,
              toParams: (values) => Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '')),
              fromParams: (stored) => ({ ...stored }),
            }} />
          {actions}
        </div>
        <div className="row row--wrap">
          <ColumnsButton all={columns} state={chooser} title={`Columns on ${title.toLowerCase()}`} />
          <Button kind="ghost" icon={<Icon name="filter" size={18} />} aria-expanded={open} onClick={() => setOpen(!open)}>
            {active > 0 ? `Filter (${active})` : 'Filter'}
          </Button>
          <Button kind="ghost" title="Export the current view to an Excel file" onClick={() => setConfirming(true)}>Export</Button>
          <span className="muted num" aria-label="Rows shown">{from}-{to} of {shown.length}</span>
          <Button kind="ghost" aria-label="Previous page" disabled={current <= 1} onClick={() => setPage(current - 1)}>‹</Button>
          <Button kind="ghost" aria-label="Next page" disabled={current >= pages} onClick={() => setPage(current + 1)}>›</Button>
        </div>
      </div>

      {open ? (
        <div className="panel" style={{ padding: 16, marginBottom: 16 }}>
          <div className="filter-bar" style={{ margin: 0 }}>
            {filters.map((f) => (
              <Field key={f.key} label={f.label}>
                {f.options ? (
                  <Select value={draft[f.key] ?? ''} placeholder="Any" options={f.options}
                    onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
                ) : (
                  <Input value={draft[f.key] ?? ''} onChange={(v) => setDraft({ ...draft, [f.key]: v })} />
                )}
              </Field>
            ))}
            <div className="row" style={{ paddingBottom: 18 }}>
              <Button kind="primary" onClick={() => { setApplied(draft); setPage(1) }}>Apply</Button>
              <Button kind="ghost" onClick={() => { setDraft({}); setApplied({}); setPage(1) }}>Clear</Button>
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div style={{ marginBottom: 12 }}><Notice kind="bad">{error}</Notice></div> : null}

      <Table
        columns={tableColumns}
        rows={slice}
        getKey={getKey}
        {...(onRowClick ? { onRowClick } : {})}
        empty={empty ?? <Empty title="Nothing to show" body={['Nothing matches these filters.']} />}
      />

      {confirming ? (
        <Modal
          title="Export to Excel"
          onClose={() => setConfirming(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
              <Button kind="primary" disabled={exporting} onClick={() => void runExport()}>Export</Button>
            </>
          }
        >
          <p>This will export all {shown.length} entries to Excel. Are you sure you want to continue?</p>
        </Modal>
      ) : null}
    </>
  )
}
